import { z } from "zod";
import { LOCALES } from "@erp/i18n";
import { MAX_AMOUNT_CENTS, VAT_RATES_BP } from "./money";

/** Validation messages are i18n keys, translated by the client. */
const requiredText = (max = 200) => z.string().trim().min(1, "errors.field.required").max(max, "errors.field.tooLong");
const optionalText = (max = 200) =>
  z
    .string()
    .trim()
    .max(max, "errors.field.tooLong")
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

/** Harmonized system code (customs): 6 to 10 digits, spaces and dots typed in are dropped. */
export const hsCodeSchema = z
  .string()
  .transform((v) => v.replace(/[\s.]/g, ""))
  .refine((v) => v === "" || /^\d{6,10}$/.test(v), "errors.field.hsCode")
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional();
/** ISO 3166-1 alpha-2 country code ("FR"), upper-cased. */
export const countryCodeSchema = z
  .string()
  .trim()
  .transform((v) => v.toUpperCase())
  .refine((v) => v === "" || /^[A-Z]{2}$/.test(v), "errors.field.country")
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional();

const optionalEmail = z
  .union([z.literal(""), z.email("errors.field.email")])
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional();

/**
 * Check key of a SIRET (INSEE): Luhn over the 14 digits. La Poste's establishments (SIREN 356 000 000)
 * use their own rule: the sum of the digits is a multiple of 5.
 */
export function isValidSiret(value: string): boolean {
  if (!/^\d{14}$/.test(value)) return false;
  if (value.startsWith("356000000")) return [...value].reduce((sum, d) => sum + Number(d), 0) % 5 === 0;
  let sum = 0;
  for (const [i, char] of [...value].toReversed().entries()) {
    const digit = Number(char) * (i % 2 ? 2 : 1);
    sum += digit > 9 ? digit - 9 : digit;
  }
  return sum % 10 === 0;
}

/** 14 digits once spaces are removed, with a valid check key (Chorus Pro refuses the others); empty means none. */
const siret = z
  .string()
  .transform((v) => v.replace(/\s/g, ""))
  .pipe(
    z.union([
      z.literal(""),
      z
        .string()
        .regex(/^\d{14}$/, "errors.field.siret")
        .refine(isValidSiret, "errors.field.siretKey"),
    ]),
  )
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional();

export { siret as siretSchema };

const vatNumber = z
  .string()
  .transform((v) => v.replace(/\s/g, "").toUpperCase())
  .pipe(z.union([z.literal(""), z.string().regex(/^[A-Z]{2}[0-9A-Z]{2,13}$/, "errors.field.vat")]))
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional();

export const countryCode = z.string().length(2).toUpperCase();
export const currencyCode = z.string().length(3).toUpperCase();

export const addressSchema = z.object({
  line1: requiredText(200),
  line2: optionalText(200),
  postalCode: requiredText(20),
  city: requiredText(120),
  country: countryCode.default("FR"),
});
export type AddressInput = z.infer<typeof addressSchema>;

export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/**
 * Purchase order and signed delivery note of an order, together: both travel twice in the invoice's
 * Factur-X (embedded in the PDF and base64 in the XML), and Chorus Pro refuses a flux over 10 MB.
 */
export const ORDER_ATTACHMENTS_MAX_BYTES = Math.round(3.5 * 1024 * 1024);
/** A file sent base64-encoded (at most 10 MB once decoded): SAV photos, customer purchase orders. */
export const attachmentSchema = z.object({
  name: z.string().trim().min(1, "errors.field.required").max(200),
  type: z.string().trim().max(120).default("application/octet-stream"),
  dataBase64: z
    .string()
    .min(1, "errors.field.required")
    .max(Math.ceil((ATTACHMENT_MAX_BYTES * 4) / 3) + 4, "errors.field.fileTooLarge"),
});
export type AttachmentInput = z.input<typeof attachmentSchema>;

export const listQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  archived: z.enum(["exclude", "include", "only"]).default("exclude"),
  sort: z.string().max(40).optional(),
  dir: z.enum(["asc", "desc"]).default("asc"),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

export interface Page<T> {
  items: T[];
  total: number;
}

// ── Customers ────────────────────────────────────────────────────────────────

export const CUSTOMER_KINDS = ["individual", "business", "public"] as const;
export type CustomerKind = (typeof CUSTOMER_KINDS)[number];

export const contactSchema = z.object({
  id: z.uuid().optional(),
  firstName: optionalText(100),
  lastName: requiredText(100),
  jobTitle: optionalText(120),
  email: optionalEmail,
  phone: optionalText(40),
  isPrimary: z.boolean().default(false),
});
export type ContactInput = z.infer<typeof contactSchema>;

export const customerSchema = z
  .object({
    kind: z.enum(CUSTOMER_KINDS),
    companyName: optionalText(200),
    firstName: optionalText(100),
    lastName: optionalText(100),
    email: optionalEmail,
    phone: optionalText(40),
    siret,
    vatNumber,
    chorusServiceCode: optionalText(100),
    paymentTermsDays: z.coerce.number().int().min(0, "errors.field.positive").max(180).default(30),
    language: z.enum(LOCALES).default("fr"),
    billingAddress: addressSchema.nullable().optional(),
    shippingAddress: addressSchema.nullable().optional(),
    notes: optionalText(5000),
    contacts: z.array(contactSchema).max(50).default([]),
  })
  .superRefine((value, ctx) => {
    if (value.kind === "individual" && !value.lastName) {
      ctx.addIssue({ code: "custom", path: ["lastName"], message: "errors.field.required" });
    }
    if (value.kind !== "individual" && !value.companyName) {
      ctx.addIssue({ code: "custom", path: ["companyName"], message: "errors.field.required" });
    }
  });
export type CustomerInput = z.input<typeof customerSchema>;
export type CustomerData = z.output<typeof customerSchema>;

export function customerDisplayName(c: {
  kind: CustomerKind;
  companyName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}): string {
  if (c.kind !== "individual" && c.companyName) return c.companyName;
  return [c.firstName, c.lastName].filter(Boolean).join(" ");
}

// ── Suppliers ────────────────────────────────────────────────────────────────

export const supplierSchema = z.object({
  name: requiredText(200),
  contactName: optionalText(200),
  email: optionalEmail,
  phone: optionalText(40),
  website: optionalText(300),
  address: addressSchema.nullable().optional(),
  currency: currencyCode.default("EUR"),
  /** Language of the purchase orders sent to this supplier. */
  language: z.enum(["fr", "en"]).default("fr"),
  leadTimeDays: z.coerce.number().int().min(0, "errors.field.positive").max(365).nullable().optional(),
  vatNumber,
  notes: optionalText(5000),
});
export type SupplierInput = z.input<typeof supplierSchema>;
export type SupplierData = z.output<typeof supplierSchema>;

// ── Products ─────────────────────────────────────────────────────────────────

export const vatRateSchema = z.coerce
  .number()
  .int()
  .refine((v) => (VAT_RATES_BP as readonly number[]).includes(v), "errors.field.number");

export const variantSchema = z.object({
  id: z.uuid().optional(),
  sku: requiredText(64).transform((v) => v.toUpperCase()),
  ean: optionalText(14),
  options: z
    .array(z.object({ name: requiredText(60), value: requiredText(120) }))
    .max(3)
    .default([]),
  weightGrams: z.coerce.number().int().min(0, "errors.field.positive").nullable().optional(),
  salePriceCents: z.coerce
    .number()
    .int("errors.field.number")
    .min(0, "errors.field.positive")
    .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge"),
  /**
   * Base cost before any receipt; later receipts average from it (CUMP). Ignored on an article that
   * has stock movements: its cost is then corrected by a revaluation (`POST /products/variants/:id/cost`).
   */
  costCents: z.coerce
    .number()
    .int("errors.field.number")
    .min(0, "errors.field.positive")
    .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge")
    .optional(),
  reorderPoint: z.coerce.number().int().min(0, "errors.field.positive").default(0),
  targetStock: z.coerce.number().int().min(0, "errors.field.positive").default(0),
  mainSupplierId: z.uuid().nullable().optional(),
});
export type VariantInput = z.input<typeof variantSchema>;

/** Correction of a stocked article's weighted average cost (written to the stock ledger). */
export const variantCostSchema = z.object({
  costCents: z.coerce
    .number()
    .int("errors.field.number")
    .min(0, "errors.field.positive")
    .max(MAX_AMOUNT_CENTS, "errors.field.amountTooLarge"),
  note: optionalText(500),
});
export type VariantCostInput = z.input<typeof variantCostSchema>;

/** Settings › Families: a family renamed (to an existing name: merged), and the order of the list. */
export const familyRenameSchema = z
  .object({ from: requiredText(120), to: requiredText(120) })
  .refine((v) => v.from !== v.to, { message: "errors.field.required", path: ["to"] });
export type FamilyRenameInput = z.input<typeof familyRenameSchema>;
export const familyOrderSchema = z.object({
  names: z
    .array(requiredText(120))
    .max(500)
    .refine((names) => new Set(names).size === names.length, "errors.field.number"),
});
export type FamilyOrderInput = z.input<typeof familyOrderSchema>;

export const productSchema = z.object({
  title: requiredText(200),
  productType: optionalText(120),
  description: optionalText(20000),
  /** Name printed on purchase orders instead of the title (suppliers mostly read English). */
  supplierLabel: optionalText(200),
  vatRateBp: vatRateSchema.default(2000),
  hsCode: hsCodeSchema,
  originCountry: countryCodeSchema,
  /** Units followed one by one (serial numbers); set through its own endpoint, kept here for the form. */
  serialTracked: z.boolean().default(false),
  variants: z.array(variantSchema).min(1, "errors.field.required").max(200),
});
export type ProductInput = z.input<typeof productSchema>;
export type ProductData = z.output<typeof productSchema>;

/**
 * Moves a variant under another product (call of 26/10: the M helmets imported as products of their
 * own belong with the L/XL web helmet). It takes the target's option axes, with a value for each.
 */
export const attachVariantSchema = z.object({
  productId: z.uuid("errors.field.required"),
  options: z.array(z.object({ name: requiredText(60), value: requiredText(120) })).max(3),
  dryRun: z.boolean().default(true),
});
export type AttachVariantInput = z.input<typeof attachVariantSchema>;
export type AttachVariantData = z.output<typeof attachVariantSchema>;
